import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Clock,
  History,
  Loader2,
  RotateCcw,
  User,
  X,
} from "lucide-react";
import { formatCurrency } from "../lib/calculations";
import {
  fetchPaymentVersions,
  revertToPaymentVersion,
  type PaymentVersionRow,
  type PaymentVersionReport,
} from "../lib/paymentVersions";
import { useToast } from "./ui/ToastModal";
import { logError } from "../lib/logError";

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface Props {
  report: PaymentVersionReport;
  onClose: () => void;
  onReverted: () => void;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(value: unknown): string {
  if (!value || typeof value !== "string") return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return "-";
  const [, year, month, day] = match;
  return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]} ${String(year).slice(-2)}`;
}

function getSnapshotNumber(
  version: PaymentVersionRow,
  key: string
): number {
  return Number(version.snapshot?.[key] ?? 0);
}

function getLineCount(version: PaymentVersionRow): number {
  const items = version.snapshot?.payment_line_items;
  return Array.isArray(items) ? items.length : 0;
}

function getBadgeStyle(versionType: string): React.CSSProperties {
  if (versionType === "revert") {
    return {
      background: "#fffbeb",
      border: "1px solid #fde68a",
      color: "#b45309",
    };
  }

  return {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#047857",
  };
}

export function PaymentHistoryDrawer({ report, onClose, onReverted }: Props) {
  const { addToast } = useToast();
  const [versions, setVersions] = useState<PaymentVersionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState(false);
  const [confirmVersion, setConfirmVersion] = useState<PaymentVersionRow | null>(null);

  const period = useMemo(() => {
    const month = Number(report.claim_month || 0);
    if (!month || !report.claim_year) return `Claim #${report.claim_number ?? "-"}`;
    return `Claim #${report.claim_number ?? "-"} · ${MONTH_SHORT[month - 1]} ${report.claim_year}`;
  }, [report.claim_month, report.claim_number, report.claim_year]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      setVersions(await fetchPaymentVersions(report.id));
    } catch (err) {
      logError("Error loading payment history:", err);
      addToast({
        type: "error",
        title: "History Failed",
        message: err instanceof Error ? err.message : "Could not load payment history",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, report.id]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleRevert = async () => {
    if (!confirmVersion) return;
    setReverting(true);
    try {
      await revertToPaymentVersion(confirmVersion);
      addToast({
        type: "success",
        title: "Payment Reverted",
        message: `Restored payment state from version ${confirmVersion.version_number}.`,
      });
      setConfirmVersion(null);
      onReverted();
    } catch (err) {
      logError("Payment revert failed:", err);
      addToast({
        type: "error",
        title: "Revert Failed",
        message: err instanceof Error ? err.message : "Could not revert payment",
      });
    } finally {
      setReverting(false);
    }
  };

  return createPortal(
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.35)",
          zIndex: 10040,
        }}
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label="Payment history"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(520px, 100vw)",
          height: "100vh",
          zIndex: 10041,
          background: "#fff",
          borderLeft: "1px solid #e2e8f0",
          boxShadow: "-20px 0 60px rgba(15,23,42,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <header
          style={{
            padding: "18px 20px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <History size={18} color="#2563eb" />
              <h3
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  color: "#0f172a",
                  fontWeight: 800,
                }}
              >
                Payment History
              </h3>
            </div>
            <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 3 }}>
              {period}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close payment history"
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </header>

        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div
              style={{
                height: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              <Loader2 size={18} className="spin" />
            </div>
          ) : versions.length === 0 ? (
            <div
              style={{
                border: "1px dashed #cbd5e1",
                borderRadius: 8,
                padding: 20,
                textAlign: "center",
                color: "#64748b",
                fontSize: "0.82rem",
              }}
            >
              No payment history has been recorded for this claim yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {versions.map((version) => {
                const badgeStyle = getBadgeStyle(version.version_type);
                const received = getSnapshotNumber(version, "paid_by_builder");
                const expected = getSnapshotNumber(version, "calculated_payment_amount");
                const difference = getSnapshotNumber(version, "difference");
                return (
                  <article
                    key={version.id}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "12px 14px", borderBottom: "1px solid #f1f5f9" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                            <span
                              style={{
                                ...badgeStyle,
                                fontSize: "0.62rem",
                                textTransform: "uppercase",
                                fontWeight: 800,
                                borderRadius: 5,
                                padding: "2px 6px",
                              }}
                            >
                              {version.version_type}
                            </span>
                            <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#0f172a" }}>
                              Version {version.version_number}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.68rem", color: "#64748b" }}>
                              <User size={11} /> {version.changed_by_name || "Unknown"}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.68rem", color: "#64748b" }}>
                              <Clock size={11} /> {formatTimestamp(version.created_at)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfirmVersion(version)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            border: "1px solid #fed7aa",
                            background: "#fff7ed",
                            color: "#c2410c",
                            borderRadius: 6,
                            padding: "5px 8px",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <RotateCcw size={12} />
                          Revert
                        </button>
                      </div>
                      <p
                        style={{
                          margin: "8px 0 0",
                          color: "#334155",
                          fontSize: "0.76rem",
                          lineHeight: 1.45,
                        }}
                      >
                        {version.changes_summary}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "10px 14px 12px",
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 8,
                        fontSize: "0.72rem",
                      }}
                    >
                      <div>
                        <div style={{ color: "#64748b" }}>Amount received</div>
                        <strong data-seq-private="" style={{ color: "#0f172a" }}>{formatCurrency(received, true)}</strong>
                      </div>
                      <div>
                        <div style={{ color: "#64748b" }}>Expected</div>
                        <strong data-seq-private="" style={{ color: "#0f172a" }}>{formatCurrency(expected, true)}</strong>
                      </div>
                      <div>
                        <div style={{ color: "#64748b" }}>Difference</div>
                        <strong data-seq-private="" style={{ color: difference > 0 ? "#d97706" : difference < 0 ? "#dc2626" : "#16a34a" }}>
                          {formatCurrency(difference, true)}
                        </strong>
                      </div>
                      <div>
                        <div style={{ color: "#64748b" }}>Date paid</div>
                        <strong style={{ color: "#0f172a" }}>{formatDateOnly(version.snapshot?.date_paid)}</strong>
                      </div>
                      <div>
                        <div style={{ color: "#64748b" }}>Line items</div>
                        <strong style={{ color: "#0f172a" }}>{getLineCount(version)}</strong>
                      </div>
                      <div>
                        <div style={{ color: "#64748b" }}>Retention released</div>
                        <strong data-seq-private="" style={{ color: "#0f172a" }}>
                          {formatCurrency(getSnapshotNumber(version, "retention_received_amount"), true)}
                        </strong>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </aside>

      {confirmVersion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10042,
            background: "rgba(15,23,42,0.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => setConfirmVersion(null)}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 24px 80px rgba(15,23,42,0.28)",
              overflow: "hidden",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={18} color="#d97706" />
                <h3 style={{ margin: 0, fontSize: "0.95rem", color: "#0f172a" }}>
                  Revert Payment Version {confirmVersion.version_number}?
                </h3>
              </div>
            </div>
            <div style={{ padding: 18 }}>
              <p style={{ margin: 0, fontSize: "0.82rem", color: "#475569", lineHeight: 1.5 }}>
                This restores the report payment fields and payment line items to the selected snapshot. A new revert version will be recorded first.
              </p>
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  fontSize: "0.75rem",
                  color: "#334155",
                }}
              >
                {confirmVersion.changes_summary}
              </div>
            </div>
            <div
              style={{
                padding: "12px 18px",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setConfirmVersion(null)}
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#475569",
                  borderRadius: 6,
                  padding: "7px 12px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevert}
                disabled={reverting}
                style={{
                  border: "none",
                  background: reverting ? "#94a3b8" : "#d97706",
                  color: "#fff",
                  borderRadius: 6,
                  padding: "7px 12px",
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  cursor: reverting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {reverting ? <Loader2 size={13} className="spin" /> : <RotateCcw size={13} />}
                {reverting ? "Reverting..." : "Revert"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
